import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Sparkles, 
  MapPin, 
  X, 
  Activity, 
  AlertTriangle, 
  RefreshCw,
  Compass,
  Check
} from "lucide-react";

// Clean, high-end bold formatting that integrates seamlessly into the typography flow
const formatBold = (text) => {
  if (!text) return "";
  
  // 1. Convert markdown bold tags (**word**) to clean high-contrast text without distracting background boxes
  let formatted = text.replace(
    /\*\*(.*?)\*\*/g, 
    '<strong class="font-bold text-slate-900">$1</strong>'
  );
  
  // 2. Identify plain text parts and automatically highlight metrics/units in professional indigo-600
  const highlightPlainText = (str) => {
    let result = str;
    
    // Auto-detect numbers with standard units: e.g. "12%", "35ft", "1,500 sqft", "0.5 acres"
    result = result.replace(
      /\b\d+(?:,\d+)*(?:\.\d+)?\s*(?:sqft|sq\.?\s*ft\.?|ft|%|acres)\b/gi,
      '<strong class="font-bold text-indigo-600">$&</strong>'
    );
    
    // Auto-detect common zoning abbreviations/keywords
    result = result.replace(
      /\b(MDA|MFA|CDP|Zoning District|Design Review)\b/g,
      '<strong class="font-semibold text-slate-900">$&</strong>'
    );
    
    return result;
  };

  // Split content by HTML tag boundaries to prevent double-wrapping tags from step 1
  const parts = formatted.split(/(<[^>]+>)/g);
  const processedParts = parts.map((part) => {
    if (part.startsWith("<") && part.endsWith(">")) {
      return part; // Return existing HTML tags unmodified
    }
    return highlightPlainText(part);
  });
  
  return processedParts.join("");
};

const MarkdownRenderer = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n');
  
  let currentSection = "opportunities";

  return (
    <div className="space-y-4">
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) return null;
        
        // Section Headers: Clean, minimalist look with left-border highlight
        if (trimmed.startsWith('###')) {
          const headerText = trimmed.replace(/^###\s*/, '');
          
          let icon = <Compass className="w-4 h-4 text-indigo-500" />;
          let borderTheme = "border-l-indigo-500";

          if (headerText.includes("Opportunities") || headerText.includes("🌟")) {
            currentSection = "opportunities";
            icon = <Sparkles className="w-4 h-4 text-amber-500" />;
            borderTheme = "border-l-amber-500";
          } else if (headerText.includes("Constraints") || headerText.includes("⚠️")) {
            currentSection = "constraints";
            icon = <AlertTriangle className="w-4 h-4 text-rose-500" />;
            borderTheme = "border-l-rose-500";
          } else if (headerText.includes("Next Steps") || headerText.includes("📋")) {
            currentSection = "steps";
            icon = <Activity className="w-4 h-4 text-emerald-500" />;
            borderTheme = "border-l-emerald-500";
          }

          return (
            <div key={idx} className={`flex items-center gap-2.5 mt-8 mb-3 first:mt-0 pl-3 border-l-2 ${borderTheme}`}>
              <div className="flex-shrink-0">
                {icon}
              </div>
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-widest">
                {headerText.replace(/[🌟⚠️📋]/g, '').trim()}
              </h4>
            </div>
          );
        }
        
        // Match lists: bullet points (- or *) or numbered lists (e.g. 1. , 2. )
        if (trimmed.startsWith('-') || trimmed.startsWith('*') || /^\d+\.\s*/.test(trimmed)) {
          let bulletText = trimmed;
          let stepNumber = null;
          
          const numMatch = trimmed.match(/^(\d+)\.\s*(.*)/);
          if (numMatch) {
            stepNumber = numMatch[1];
            bulletText = numMatch[2];
          } else {
            bulletText = trimmed.replace(/^[-*]\s*/, '');
          }
          
          let borderTheme = "border-l-indigo-400";
          let dotElement = <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 shrink-0" />;
          let wrapperClass = "p-4 rounded-xl border border-slate-100 bg-white border-l-2 shadow-sm flex items-start gap-3";
          
          if (currentSection === "opportunities") {
            borderTheme = "border-l-amber-400";
          }
          if (currentSection === "constraints") {
            borderTheme = "border-l-rose-400";
          }
          if (currentSection === "steps") {
            borderTheme = "border-l-emerald-400";
            
            if (stepNumber) {
              // Numbered checklist circle for a highly professional roadmap flow
              dotElement = (
                <div className="w-6 h-6 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0 text-xs font-extrabold text-emerald-700 shadow-sm mt-0.5">
                  {stepNumber}
                </div>
              );
            } else {
              dotElement = (
                <div className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                  <Check className="w-3 h-3 text-emerald-600 stroke-[3.5]" />
                </div>
              );
            }
            wrapperClass = "p-4 rounded-xl border border-slate-100 bg-white border-l-2 shadow-sm flex items-center gap-3.5 hover:border-slate-200 transition-all";
          }

          return (
            <div 
              key={idx}
              className={`${wrapperClass} ${borderTheme}`}
            >
              {dotElement}
              <span className="flex-1 text-slate-600 font-normal text-[13px] leading-relaxed" dangerouslySetInnerHTML={{ __html: formatBold(bulletText) }} />
            </div>
          );
        }
        
        // Clean, elegant plain text paragraphs (no distracting card border/background wrapper)
        return (
          <p key={idx} className="text-slate-500 font-medium text-[13px] leading-relaxed py-1.5 px-1" dangerouslySetInnerHTML={{ __html: formatBold(trimmed) }} />
        );
      })}
    </div>
  );
};

const AINarrativeModal = ({ isOpen, onClose, analysis, onGenerate, isGenerating }) => {
  if (!isOpen || !analysis) return null;

  // Robust field fallbacks to prevent N/A values if property keys have variations
  const zoningCode = analysis.zone_code || 
                     analysis.parcel_zone_code || 
                     (analysis.notes && (analysis.notes.zone_code || analysis.notes.parcel_zone_code)) || 
                     "R-1";

  const mfaVal = analysis.mfa_ft2 || (analysis.notes && (analysis.notes.mfa_ft2 || analysis.notes.mfa_limit));
  const mfa = mfaVal ? `${Number(mfaVal).toLocaleString()} sqft` : "N/A";

  const mdaVal = analysis.mda_ft2 || (analysis.notes && (analysis.notes.mda_ft2 || analysis.notes.mda_limit));
  const mda = mdaVal ? `${Number(mdaVal).toLocaleString()} sqft` : "N/A";

  const slopeVal = analysis.avg_slope_percent_LAH !== undefined 
    ? analysis.avg_slope_percent_LAH 
    : (analysis.slope_percent !== undefined ? analysis.slope_percent : (analysis.notes?.avg_slope_percent_LAH));
  const slope = slopeVal !== undefined && slopeVal !== null ? `${Number(slopeVal).toFixed(1)}%` : "N/A";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex justify-end">
        {/* Backdrop (Dark dim with light blur) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
        />

        {/* Clean, high-end corporate side drawer (max-w-3xl) */}
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.35, ease: "easeInOut" }}
          className="relative w-full max-w-3xl bg-white shadow-2xl flex flex-col h-full z-10 border-l border-slate-200"
        >
          {/* Header */}
          <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
            <div className="flex items-center gap-3">
              <div className="bg-slate-50 p-2 rounded-xl border border-slate-200">
                <Sparkles className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">AI Feasibility Agent</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 tracking-tight leading-none mt-1">Property Narrative</h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-xl transition-all border border-slate-200/50"
            >
              <X className="w-4.5 h-4.5" />
            </button>
          </div>

          {/* Drawer Body */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6 bg-slate-50/40">
            
            {/* Target Asset Detail Bento Card */}
            <div className="bg-white rounded-2xl p-5 border border-slate-150 shadow-sm relative">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-4 border-b border-slate-100 mb-4">
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Property Address</span>
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mt-0.5">
                    <MapPin className="w-4 h-4 text-indigo-500 shrink-0" />
                    {analysis.address}
                  </h4>
                </div>
                {analysis.county && (
                  <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded border border-slate-200 uppercase tracking-wider shrink-0">
                    {analysis.county} County
                  </span>
                )}
              </div>

              {/* Minimalist Bento Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Zoning Code</span>
                  <span className="text-sm font-bold text-slate-800 mt-1 block">{zoningCode}</span>
                </div>
                <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Max Floor Area</span>
                  <span className="text-sm font-bold text-slate-800 mt-1 block">{mfa}</span>
                </div>
                <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Max Dev. Area</span>
                  <span className="text-sm font-bold text-slate-800 mt-1 block">{mda}</span>
                </div>
                <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Avg Slope</span>
                  <span className="text-sm font-bold text-slate-800 mt-1 block">{slope}</span>
                </div>
              </div>
            </div>

            {/* AI Narrative Content */}
            <div className="bg-white rounded-2xl border border-slate-150 p-6 shadow-sm min-h-[300px]">
              {isGenerating ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="relative mb-5">
                    <div className="w-16 h-16 border-4 border-slate-100 border-t-slate-800 rounded-full animate-spin" />
                    <Sparkles className="absolute inset-0 m-auto w-5 h-5 text-slate-800 animate-pulse" />
                  </div>
                  <h5 className="text-sm font-bold text-slate-800 mb-1">Generating Feasibility Summary...</h5>
                  <p className="text-xs text-slate-400 font-medium max-w-xs leading-relaxed">
                    Analyzing zoning district codes, MDA/MFA capacity, setbacks limits, and topographic slopes.
                  </p>
                </div>
              ) : analysis.notes?.ai_summary ? (
                <MarkdownRenderer content={analysis.notes.ai_summary} />
              ) : (
                <div className="text-center py-16">
                  <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                  <h5 className="text-sm font-bold text-slate-700 mb-1">No summary generated yet</h5>
                  <p className="text-xs text-slate-400 font-medium mb-6 max-w-xs mx-auto leading-relaxed">
                    Request an AI-generated site opportunity and development constraints feasibility analysis.
                  </p>
                  <button
                    onClick={onGenerate}
                    className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-[11px] uppercase tracking-wider shadow-md hover:scale-[1.02] transition-all flex items-center gap-2 mx-auto"
                  >
                    <Sparkles className="w-4.5 h-4.5" />
                    <span>Generate summary</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Drawer Footer */}
          <div className="px-8 py-4 border-t border-slate-200/80 bg-white flex justify-between items-center shrink-0">
            <div className="flex flex-col">
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Engine</span>
              <span className="text-[10px] font-semibold text-slate-500">llama-3.3-70b-versatile</span>
            </div>
            
            <div className="flex gap-2">
              {analysis.notes?.ai_summary && !isGenerating && (
                <button
                  onClick={onGenerate}
                  className="px-4 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-colors flex items-center gap-1.5"
                  title="Regenerate Narrative"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Regenerate
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AINarrativeModal;
